import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { Stethoscope, Bone, Brain, Copy, Mail, FileDown } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const WIN_RATE = 0.88;
const ADMIN_RATE = 0.22;

const SPECIALTIES = [
  {
    id: "general",
    label: "General / OBGYN",
    icon: Stethoscope,
    color: "#34D399",
    valueMin: 30000, valueMax: 100000,
    costMin: 1000, costMax: 3000,
    defaults: { claims: 10, value: 60000, cost: 2000 },
  },
  {
    id: "ortho",
    label: "Ortho Non-Spine & Higher",
    icon: Bone,
    color: "#FBBF24",
    valueMin: 40000, valueMax: 180000,
    costMin: 2000, costMax: 4000,
    defaults: { claims: 6, value: 100000, cost: 3000 },
  },
  {
    id: "spine",
    label: "Spine / Neuro",
    icon: Brain,
    color: "#22D3EE",
    valueMin: 70000, valueMax: 300000,
    costMin: 3000, costMax: 6000,
    defaults: { claims: 4, value: 180000, cost: 4500 },
  },
];

const calc = (row) => {
  const won = row.claims * row.value * 12 * WIN_RATE;
  const assist = row.claims * 12 * row.cost;
  return { won, assist };
};

function SpecialtyCard({ spec, row, onChange }) {
  const Icon = spec.icon;
  const { won, assist } = calc(row);
  const active = row.claims > 0;
  return (
    <div
      className={`rounded-xl border p-8 transition-colors duration-300 ${
        active ? "border-emerald-500/30 bg-[#121722]" : "border-white/10 bg-[#121722]/60"
      }`}
      data-testid={`specialty-card-${spec.id}`}
    >
      <div className="flex items-center gap-3">
        <span className={`flex items-center justify-center w-10 h-10 rounded-full border ${active ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}>
          <Icon className={`w-5 h-5 ${active ? "text-emerald-400" : "text-slate-500"}`} />
        </span>
        <div>
          <h3 className="text-sm font-bold text-slate-100">{spec.label}</h3>
          <p className="text-[11px] text-slate-500">Case value {fmt(spec.valueMin)}–{fmt(spec.valueMax)} · Case cost {fmt(spec.costMin)}–{fmt(spec.costMax)}</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <div className="flex items-baseline justify-between mb-4 gap-2">
            <label className="text-xs font-semibold text-slate-300">Cases / month</label>
            <Input
              type="number"
              min={0}
              max={200}
              value={row.claims}
              onChange={(e) => onChange({ ...row, claims: Math.min(200, Math.max(0, Number(e.target.value) || 0)) })}
              className="w-20 h-9 text-right font-serif-display text-lg font-bold text-emerald-400 bg-[#0A0D14] border-white/10 focus:ring-2 focus:ring-emerald-500/50"
              data-testid={`claims-input-${spec.id}`}
            />
          </div>
          <Slider value={[row.claims]} onValueChange={([v]) => onChange({ ...row, claims: v })} min={0} max={200} step={1} data-testid={`claims-slider-${spec.id}`} />
          <div className="flex justify-between mt-2 text-[11px] text-slate-500"><span>0</span><span>200</span></div>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <label className="text-xs font-semibold text-slate-300">Case value</label>
            <span className="font-serif-display text-2xl font-bold text-emerald-400" data-testid={`amount-value-${spec.id}`}>{fmt(row.value)}</span>
          </div>
          <Slider value={[row.value]} onValueChange={([v]) => onChange({ ...row, value: v })} min={spec.valueMin} max={spec.valueMax} step={1000} data-testid={`amount-slider-${spec.id}`} />
          <div className="flex justify-between mt-2 text-[11px] text-slate-500"><span>{fmt(spec.valueMin)}</span><span>{fmt(spec.valueMax)}</span></div>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <label className="text-xs font-semibold text-slate-300">Assist cost / case</label>
            <span className="font-serif-display text-2xl font-bold text-amber-400" data-testid={`assist-value-${spec.id}`}>{fmt(row.cost)}</span>
          </div>
          <Slider value={[row.cost]} onValueChange={([v]) => onChange({ ...row, cost: v })} min={spec.costMin} max={spec.costMax} step={100} data-testid={`assist-slider-${spec.id}`} />
          <div className="flex justify-between mt-2 text-[11px] text-slate-500"><span>{fmt(spec.costMin)}</span><span>{fmt(spec.costMax)}</span></div>
        </div>
      </div>

      <div className="mt-8 pt-5 border-t border-amber-400/40 flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Recovers / yr</p>
        <p className={`font-serif-display text-2xl font-bold ${active ? "text-emerald-400" : "text-slate-600"}`} data-testid={`subtotal-${spec.id}`}>
          {fmt(Math.max(0, won - assist))}
        </p>
      </div>
    </div>
  );
}

export default function Calculator() {
  const [rows, setRows] = useState(
    Object.fromEntries(SPECIALTIES.map((s) => [s.id, { ...s.defaults }]))
  );

  const totals = SPECIALTIES.reduce(
    (acc, s) => {
      const { won, assist } = calc(rows[s.id]);
      acc.won += won;
      acc.assist += assist;
      acc.claims += rows[s.id].claims;
      return acc;
    },
    { won: 0, assist: 0, claims: 0 }
  );
  const adminAnnual = totals.won * ADMIN_RATE;
  const netAnnual = totals.won - totals.assist - adminAnnual;
  const netMonthly = netAnnual / 12;

  const specNets = SPECIALTIES.map((s) => {
    const { won, assist } = calc(rows[s.id]);
    return { ...s, net: Math.max(0, won - assist) };
  });
  const netSum = specNets.reduce((a, s) => a + s.net, 0);

  const shareText = () => {
    const lines = [
      "ECH IDR — Annual Recovery Breakdown",
      "",
      ...specNets.map((s) => `${s.label}: ${rows[s.id].claims} cases/mo × ${fmt(rows[s.id].value)} (assist ${fmt(rows[s.id].cost)}/case) → ${fmt(s.net)}/yr`),
      "",
      `Gross awards won annually (88%): ${fmt(totals.won)}`,
      `Annual surgical assist cost: −${fmt(totals.assist)}`,
      `Annual Billing, Legal & Admin (22%): −${fmt(adminAnnual)}`,
      `Net per month: ${fmt(Math.max(0, netMonthly))}`,
      "",
      `ESTIMATED NET ANNUAL RECOVERY: ${fmt(Math.max(0, netAnnual))}`,
      "",
      "Estimates only — not a guarantee of recovery.",
    ];
    return lines.join("\n");
  };

  const copyBreakdown = async () => {
    try {
      await navigator.clipboard.writeText(shareText());
      toast.success("Breakdown copied to clipboard");
    } catch {
      toast.error("Couldn't copy — try the email option");
    }
  };

  const emailBreakdown = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent("My IDR recovery numbers")}&body=${encodeURIComponent(shareText())}`;
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = doc.internal.pageSize.getWidth();
    const M = 56;
    let y = 64;

    doc.setFillColor(10, 13, 20);
    doc.rect(0, 0, W, 110, "F");
    doc.setFont("times", "bold");
    doc.setFontSize(22);
    doc.setTextColor(248, 250, 252);
    doc.text("ECH IDR", M, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(52, 211, 153);
    doc.text("ANNUAL RECOVERY BREAKDOWN", M, y + 18);
    doc.setTextColor(148, 163, 184);
    doc.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), W - M, y + 18, { align: "right" });

    y = 150;
    doc.setTextColor(30, 41, 59);
    specNets.forEach((s) => {
      const r = rows[s.id];
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(s.label, M, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`${r.claims} cases/mo  ·  Case value ${fmt(r.value)}  ·  Assist cost ${fmt(r.cost)}/case`, M, y + 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(5, 150, 105);
      doc.text(`${fmt(s.net)} / yr`, W - M, y, { align: "right" });
      doc.setDrawColor(226, 232, 240);
      doc.line(M, y + 30, W - M, y + 30);
      doc.setTextColor(30, 41, 59);
      y += 52;
    });

    y += 10;
    const rowsData = [
      ["Gross awards won annually (88%)", fmt(totals.won), [30, 41, 59]],
      ["Annual surgical assist cost", `-${fmt(totals.assist)}`, [217, 119, 6]],
      ["Annual Billing, Legal & Admin (22%)", `-${fmt(adminAnnual)}`, [217, 119, 6]],
      ["Net per month", fmt(Math.max(0, netMonthly)), [30, 41, 59]],
    ];
    doc.setFontSize(11);
    rowsData.forEach(([label, val, col]) => {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text(label, M, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...col);
      doc.text(val, W - M, y, { align: "right" });
      y += 24;
    });

    y += 14;
    doc.setFillColor(236, 253, 245);
    doc.roundedRect(M, y - 8, W - 2 * M, 72, 6, 6, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(5, 150, 105);
    doc.text("ESTIMATED NET ANNUAL RECOVERY", M + 20, y + 14);
    doc.setFont("times", "bold");
    doc.setFontSize(28);
    doc.setTextColor(4, 120, 87);
    doc.text(fmt(Math.max(0, netAnnual)), M + 20, y + 46);

    y += 100;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    const disclaimer =
      "All figures are illustrative estimates only and do not constitute a guarantee, projection, or promise of recovery, reimbursement, or financial performance. " +
      "Win-rate assumptions are drawn from publicly published CMS federal IDR data and Georgetown CHIR analyses and reflect industry-wide averages, not the performance of any specific provider, practice, or service. " +
      "Actual outcomes vary by payer, claim eligibility, specialty, geography, filing quality, and timing. Nothing herein constitutes legal, financial, billing, or medical advice.";
    doc.text(doc.splitTextToSize(disclaimer, W - 2 * M), M, y);

    doc.save("ECH-IDR-recovery-breakdown.pdf");
    toast.success("PDF downloaded");
  };

  return (
    <section id="calculator" className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-[#0A0D14] overflow-hidden" data-testid="calculator-section">
      <div className="absolute right-0 top-1/4 w-[600px] h-[600px] rounded-full bg-emerald-500/20 blur-3xl opacity-20 pointer-events-none" />
      <div className="grain-overlay" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-400 font-semibold mb-4">Your numbers</p>
        <h2 className="font-serif-display text-3xl md:text-4xl font-bold tracking-tight text-slate-50 max-w-2xl" data-testid="calculator-heading">
          See what properly filed IDR could recover
        </h2>
        <p className="mt-5 max-w-xl text-sm md:text-base text-slate-400 leading-relaxed">
          Set the volume, case value, and surgical assist cost for each specialty your practice runs.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Specialty rows */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-8 space-y-6"
          >
            {SPECIALTIES.map((s) => (
              <SpecialtyCard
                key={s.id}
                spec={s}
                row={rows[s.id]}
                onChange={(r) => setRows((prev) => ({ ...prev, [s.id]: r }))}
              />
            ))}
            <p className="text-xs text-slate-500 leading-relaxed px-1">
              Assumes the published 88% win rate for properly filed federal IDR disputes and that a surgical assist
              is used on every eligible case. Set a specialty to 0 cases to exclude it. Estimates only — not a
              performance claim or a guarantee of recovery.
            </p>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-4"
          >
            <div className="lg:sticky lg:top-24 rounded-xl border border-emerald-500/30 bg-[#1A2130] p-8 lg:p-10" data-testid="results-card">
              <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Annual recovery breakdown</p>
              <p className="mt-2 text-xs text-slate-500" data-testid="results-claims-label">{totals.claims} cases/mo across all specialties</p>

              <div className="mt-8 space-y-4 text-sm">
                {SPECIALTIES.map((s) => {
                  const { won, assist } = calc(rows[s.id]);
                  return (
                    <div key={s.id} className="flex justify-between" data-testid={`results-row-${s.id}`}>
                      <span className="text-slate-500">{s.label}</span>
                      <span className={rows[s.id].claims > 0 ? "text-slate-300 font-semibold" : "text-slate-600"}>
                        {fmt(Math.max(0, won - assist))}
                      </span>
                    </div>
                  );
                })}
                <div className="border-b-2 border-amber-400/40" />

                {/* Recovery contribution chart */}
                <div className="pt-1 pb-3" data-testid="recovery-chart">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">Contribution to annual total</p>
                  <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-white/5">
                    {netSum > 0 && specNets.map((s) => (
                      <motion.div
                        key={s.id}
                        animate={{ width: `${(s.net / netSum) * 100}%` }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        style={{ backgroundColor: s.color }}
                        data-testid={`chart-bar-${s.id}`}
                      />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                    {specNets.map((s) => (
                      <span key={s.id} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: s.color }} />
                        {s.label.split(" ")[0]} {netSum > 0 ? Math.round((s.net / netSum) * 100) : 0}%
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between border-b-2 border-amber-400/40 pb-4">
                  <span className="text-slate-400">Gross awards won annually (88%)</span>
                  <span className="text-slate-100 font-semibold" data-testid="won-annual-value">{fmt(totals.won)}</span>
                </div>
                <div className="flex justify-between border-b-2 border-amber-400/40 pb-4">
                  <span className="text-slate-400">Annual surgical assist cost</span>
                  <span className="text-amber-400 font-semibold" data-testid="assist-annual-value">−{fmt(totals.assist)}</span>
                </div>
                <div className="flex justify-between border-b-2 border-amber-400/40 pb-4" data-testid="admin-cut-row">
                  <span className="text-slate-400">Annual Billing, Legal &amp; Admin (22%)</span>
                  <span className="text-amber-400 font-semibold" data-testid="admin-cut-value">−{fmt(adminAnnual)}</span>
                </div>
                <div className="flex justify-between pb-2">
                  <span className="text-slate-400">Net per month</span>
                  <span className="text-slate-100 font-semibold" data-testid="net-monthly-value">{fmt(Math.max(0, netMonthly))}</span>
                </div>
              </div>

              <div className="mt-6 pt-8 border-t-2 border-[#0A0D14]">
                <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Estimated net annual recovery</p>
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={netAnnual}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-3 font-serif-display text-4xl xl:text-5xl font-black tracking-tight text-emerald-400 [text-shadow:0_0_40px_rgba(16,185,129,0.35)] break-words"
                    data-testid="net-annual-value"
                  >
                    {fmt(Math.max(0, netAnnual))}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  <button
                    onClick={copyBreakdown}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] hover:bg-emerald-500/20 transition-colors duration-200"
                    data-testid="share-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy numbers
                  </button>
                  <button
                    onClick={emailBreakdown}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-slate-300 px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] hover:border-white/30 hover:text-slate-100 transition-colors duration-200"
                    data-testid="share-email-btn"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Email them
                  </button>
                  <button
                    onClick={downloadPdf}
                    className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 text-[#0A0D14] px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] hover:bg-emerald-400 transition-colors duration-200"
                    data-testid="download-pdf-btn"
                  >
                    <FileDown className="w-4 h-4" />
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
